import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
    CloudUploadOutlined, 
    DeleteOutlined, 
    EditOutlined, 
    PlusOutlined,
    ToolOutlined,
    DatabaseOutlined,
    FileSearchOutlined,
    AuditOutlined,
    LinkOutlined,
    CodeOutlined,
    ExperimentOutlined,
    PlayCircleOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import { PageContainer } from '@ant-design/pro-components';
import { 
    BreadcrumbProps, 
    Button, 
    Card, 
    Empty, 
    Row, 
    Col, 
    Typography, 
    Space, 
    Input, 
    Avatar, 
    Modal,
    Carousel,
    Image,
    Tag
} from 'antd';
import { useRequest } from 'ahooks';

import { ITool, IToolMedia } from 'models/tool';
import { SimpleDate } from 'components/helpers/date';
import { breadCrumbItemRender } from 'components/helpers/breadcrumbs';
import { ActionMenuItemMap, ActionsMenu } from 'components/actions';
import { TEST_TOOL_ID } from 'utils/constants';

import { getTools } from 'api/tools';
import { getUserId } from 'utils/auth';

const { Text, Title, Paragraph } = Typography;
const { Search } = Input;

enum ToolTab {
    All = 'all',
    Mine = 'mine',
}

// Map tool names to their corresponding icons
const toolIcons: Record<string, React.ReactNode> = {
    'Query Builder': <CodeOutlined />,
    'Power Audit': <AuditOutlined />,
    'Solutions Viewer': <FileSearchOutlined />,
    'Web Resources Viewer': <LinkOutlined />,
    'Dataflow Manager': <DatabaseOutlined />,
    'default': <ToolOutlined />
};

export const ToolList: React.FC = () => {
    const location = useLocation();
    const [ activeTabKey, setActiveTabKey ] = useState<ToolTab>(ToolTab.All);
    const [ searchQuery, setSearchQuery ] = useState('');
    const [ selectedTool, setSelectedTool ] = useState<ITool | null>(null);
    const { data, error, loading, run } = useRequest(getTools, { manual: true });
    const navigate = useNavigate();

    // Load tools on mount and tab change
    useEffect(() => {
        const userId = getUserId();
        run(activeTabKey === ToolTab.Mine && userId ? userId : '');
    }, [activeTabKey, run]);

    useEffect(() => {
        const s = new URLSearchParams(location.search.replace('?', ''));
        const toTab = s.get('tab');
        if (!toTab || toTab === activeTabKey || !Object.values(ToolTab).includes(toTab as ToolTab)) {
            return;
        }
        setActiveTabKey(toTab as ToolTab);
    }, [location, activeTabKey]);

    const myActions: ActionMenuItemMap<ITool> = {
        edit: { icon: <EditOutlined />, text: 'Edit' },
        update: { icon: <CloudUploadOutlined />, text: 'Upload Update' },
        delete: { icon: <DeleteOutlined />, text: 'Delete', disabled: () => true },
    };

    const onActionClick = async (tool: ITool, what: string) => {
        console.log(`Clicked on '${ what }' for the tool: ${ tool.name }`, tool);

        switch (what) {
            case 'edit':
                return;
            case 'update':
                navigate(`/tools/update/${ tool.id }`);
                return;
        }
    };

    const breadcrumbProps: BreadcrumbProps = {
        itemRender: breadCrumbItemRender,
        routes: [
            {
                path: `/`,
                breadcrumbName: 'Dashboard',
            },
            {
                path: `/tools`,
                breadcrumbName: `Tools`,
            },
        ],
    };

    const tabList = [
        { tab: 'All Tools', key: ToolTab.All },
        { tab: 'Tools By Me', key: ToolTab.Mine },
    ];

    const onTabChange = (tabKey: string) => {
        navigate(`/tools?tab=${ tabKey }`);
    };

    if (error) {
        console.warn('error while fetching the tools:', error, data);
    }

    const filteredTools = useMemo(() => {
        if (!data) return [];
        
        let filtered = [...data];
        
        // Sort by modification date (newest first)
        filtered.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        
        // Filter by search query
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(tool => 
                tool.name.toLowerCase().includes(query) || 
                tool.description.toLowerCase().includes(query)
            );
        }
        
        return filtered;
    }, [data, searchQuery]);

    const renderMedia = (media: IToolMedia[]) => {
        if (!media?.length) return null;

        return (
            <Carousel autoplay>
                {media.map((item) => (
                    <div key={item.id}>
                        {item.type === 'video' ? (
                            <video 
                                controls 
                                style={{ width: '100%', maxHeight: '400px', objectFit: 'contain' }}
                                poster={item.thumbnailUrl}
                            >
                                <source src={item.url} type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                        ) : (
                            <Image
                                src={item.url}
                                alt="Tool screenshot"
                                style={{ width: '100%', maxHeight: '400px', objectFit: 'contain' }}
                            />
                        )}
                    </div>
                ))}
            </Carousel>
        );
    };

    const renderToolCard = (tool: ITool) => (
        <Col xs={24} sm={12} lg={8} key={tool.id}>
            <Card
                hoverable
                onClick={() => navigate(`/tools/${tool.id}`)}
                style={{ 
                    aspectRatio: '1',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '16px'
                }}
                bodyStyle={{ 
                    padding: '16px',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <div style={{ position: 'relative', flex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            zIndex: 1,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            padding: '8px',
                            borderRadius: '4px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Button
                            key="info"
                            type="text"
                            icon={<InfoCircleOutlined style={{ fontSize: '20px' }} />}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTool(tool);
                            }}
                            title="More info"
                        />
                    </div>
                    <Space 
                        style={{ 
                            position: 'absolute', 
                            top: 0, 
                            right: 0, 
                            zIndex: 1,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            padding: '8px',
                            borderRadius: '4px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Button
                            key="use"
                            type="primary"
                            icon={<PlayCircleOutlined style={{ fontSize: '20px' }} />}
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/tools/${tool.id}`);
                            }}
                            title="Use tool"
                        />
                        {activeTabKey === ToolTab.Mine && (
                            <ActionsMenu<ITool> record={tool} actions={myActions} onClick={onActionClick} />
                        )}
                    </Space>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', paddingBottom: '25px', paddingTop: '50px' }}>
                        <Space align="center" style={{ marginBottom: '24px' }}>
                            <Avatar 
                                size={64} 
                                icon={toolIcons[tool.name] || toolIcons.default}
                                style={{ 
                                    backgroundColor: '#1890ff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            />
                            <Title level={3} style={{ margin: 0 }}>{tool.name}</Title>
                        </Space>
                        <div style={{ flex: 1, overflow: 'hidden' }}>
                            <Paragraph 
                                type="secondary" 
                                style={{ 
                                    fontSize: '18px', 
                                    lineHeight: '1.5',
                                    marginBottom: 0
                                }}
                                ellipsis={{
                                    rows: 4,
                                    tooltip: tool.description,
                                }}
                            >
                                {tool.description}
                            </Paragraph>
                        </div>
                    </div>
                    <div style={{ 
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        padding: '4px 16px',
                        borderTop: '1px solid #f0f0f0',
                        backgroundColor: '#fff',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderBottomLeftRadius: '16px',
                        borderBottomRightRadius: '16px'
                    }}>
                        <Text type="secondary" style={{ fontSize: '16px' }}><SimpleDate date={tool.updatedAt} /></Text>
                        <Text type="secondary" style={{ fontSize: '16px' }}>v{tool.latestVersion?.version}</Text>
                        {activeTabKey === ToolTab.Mine && (
                            <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)' }}>
                                <ActionsMenu<ITool> record={tool} actions={myActions} onClick={onActionClick} />
                            </div>
                        )}
                    </div>
                </div>
            </Card>
        </Col>
    );

    return (
        <PageContainer
            breadcrumb={breadcrumbProps}
            extra={[
                <Search
                    key="search"
                    placeholder="Search tools..."
                    allowClear
                    onSearch={value => setSearchQuery(value)}
                    onChange={e => setSearchQuery(e.target.value)}
                    style={{ width: 300, marginRight: 16 }}
                />,
                activeTabKey === ToolTab.Mine && (
                    <Button
                        key="test"
                        onClick={() => navigate(`/tools/${TEST_TOOL_ID}`)}
                        icon={<ExperimentOutlined />}
                        style={{ marginRight: 8 }}
                    >
                        Start Testing
                    </Button>
                ),
                activeTabKey === ToolTab.Mine && (
                    <Button
                        key="new"
                        type="primary"
                        onClick={() => navigate('/tools/new')}
                        icon={<PlusOutlined />}
                    >
                        New
                    </Button>
                )
            ].filter(Boolean)}
            tabActiveKey={activeTabKey}
            tabList={tabList}
            onTabChange={onTabChange}
        >
            {loading ? (
                <Row gutter={[24, 24]}>
                    {[1, 2, 3].map((i) => (
                        <Col xs={24} sm={12} lg={8} key={i}>
                            <Card loading style={{ aspectRatio: '1', borderRadius: '16px' }} />
                        </Col>
                    ))}
                </Row>
            ) : filteredTools?.length ? (
                <Row gutter={[24, 24]}>
                    {filteredTools.map(renderToolCard)}
                </Row>
            ) : (
                <Empty 
                    description={searchQuery ? "No tools match your search" : "No tools in the system"} 
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            )}

            <Modal
                title={
                    <Space>
                        <Avatar 
                            size={32} 
                            icon={selectedTool ? (toolIcons[selectedTool.name] || toolIcons.default) : null}
                            style={{ 
                                backgroundColor: '#1890ff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        />
                        <span>{selectedTool?.name}</span>
                        <Tag color="blue">v{selectedTool?.latestVersion?.version}</Tag>
                    </Space>
                }
                visible={!!selectedTool}
                onCancel={() => setSelectedTool(null)}
                width={800}
                footer={[
                    <Button key="back" onClick={() => setSelectedTool(null)}>
                        Close
                    </Button>,
                    <Button 
                        key="use" 
                        type="primary" 
                        onClick={() => {
                            if (selectedTool) {
                                navigate(`/tools/${selectedTool.id}`);
                                setSelectedTool(null);
                            }
                        }}
                    >
                        Use Tool
                    </Button>
                ]}
            >
                {selectedTool && (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        {selectedTool.media && renderMedia(selectedTool.media)}
                        <Paragraph>
                            {selectedTool.longDescription || selectedTool.description}
                        </Paragraph>
                        <Space>
                            <Text type="secondary">Author: {selectedTool.author}</Text>
                            <Text type="secondary">•</Text>
                            <Text type="secondary">Last updated: <SimpleDate date={selectedTool.updatedAt} /></Text>
                        </Space>
                    </Space>
                )}
            </Modal>
        </PageContainer>
    );
}
